import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { notificationsApiService } from '../api/notificationsApi';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Bildirimleri yükle
  const loadNotifications = useCallback(async (params = {}) => {
    if (!isAuthenticated) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Limit ekle - çok fazla istek yapmasın
      const requestParams = { ...params, limit: 20, is_archived: false };
      const response = await notificationsApiService.getNotifications(requestParams);
      
      if (response && response.success) {
        setNotifications(response.data || []);
      } else {
        setError(response?.message || 'Bildirimler yüklenemedi');
      }
    } catch (err) {
      // Silently handle error
      setError('Bildirimler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Okunmamış sayıyı yükle
  const loadUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await notificationsApiService.getUnreadCount();
      
      if (response && response.success) {
        const count = response.count || 0;
        setUnreadCount(count);
      } else {
        setUnreadCount(0);
      }
    } catch (err) {
      // Silently handle error
      setUnreadCount(0);
    }
  }, [isAuthenticated]);

  // Bildirimi okundu olarak işaretle
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const response = await notificationsApiService.markAsRead(notificationId);
      
      if (response.success) {
        // Local state'i güncelle
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, is_read: true, read_at: new Date().toISOString() }
              : notification
          )
        );
        
        // Okunmamış sayıyı güncelle
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      // Silently handle error
    }
  }, []);

  // Tüm bildirimleri okundu olarak işaretle
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await notificationsApiService.markAllAsRead();
      
      if (response.success) {
        // Local state'i güncelle
        setNotifications(prev => 
          prev.map(notification => ({
            ...notification,
            is_read: true,
            read_at: new Date().toISOString()
          }))
        );
        
        setUnreadCount(0);
      }
    } catch (err) {
      // Silently handle error
    }
  }, []);

  // Bildirimi arşivle
  const archiveNotification = useCallback(async (notificationId) => {
    try {
      const response = await notificationsApiService.archiveNotification(notificationId);
      
      if (response.success) {
        // Local state'den kaldır
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        
        // Eğer okunmamışsa sayıyı güncelle
        const notification = notifications.find(n => n.id === notificationId);
        if (notification && !notification.is_read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (err) {
      // Silently handle error
    }
  }, [notifications]);


  // Browser notification izni iste
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  }, []);

  // İlk yükleme ve periyodik güncelleme
  useEffect(() => {
    if (isAuthenticated) {
      // İlk yükleme
      const timer = setTimeout(() => {
        loadNotifications();
        loadUnreadCount();
      }, 1000);
      
      // Her 5 dakikada bir güncelle (daha az sıklıkta)
      const interval = setInterval(() => {
        // Sadece sayfa aktifken yenile
        if (!document.hidden) {
          loadNotifications();
          loadUnreadCount();
        }
      }, 300000); // 5 dakika
      
      // Sayfa aktifken yenile
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          loadNotifications();
          loadUnreadCount();
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      return () => {
        clearTimeout(timer);
        clearInterval(interval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated]); // Sadece isAuthenticated dependency


  // Manuel yenileme fonksiyonu
  const refreshNotifications = useCallback(async () => {
    if (isAuthenticated) {
      await loadNotifications();
      await loadUnreadCount();
    }
  }, [isAuthenticated, loadNotifications, loadUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    isLoading,
    error,
    loadNotifications,
    loadUnreadCount,
    refreshNotifications, // Yeni eklenen
    markAsRead,
    markAllAsRead,
    archiveNotification,
    requestNotificationPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
